import { useEffect, useState, useCallback } from "react";
import { Cookie, ShieldCheck, X, ChevronDown } from "lucide-react";

/**
 * Bannière cookies conforme RGPD + Google Consent Mode v2.
 *
 * Comportement :
 * - Avant tout choix : tous les consent flags = "denied" (defaults poussés à gtag('consent','default'))
 * - Trois choix visibles avec le même poids : Accepter / Refuser / Personnaliser
 * - Choix conservé 6 mois (cookie 'dp_consent_v1')
 * - Réouverture manuelle via window.__openCookieBanner() (bouton "Gérer mes cookies" du footer)
 * - AUCUN tag Google Ads / Analytics avant consentement
 */

const COOKIE_NAME = "dp_consent_v1";
const COOKIE_DAYS = 180; // 6 mois

const CATEGORIES = [
  { key: "essential", label: "Essentiels", locked: true, desc: "Cookies techniques indispensables (session, sécurité, préférences)." },
  { key: "analytics", label: "Statistiques", desc: "Nous aident à mesurer l'audience et améliorer l'expérience. Ex : Google Analytics." },
  { key: "advertising", label: "Publicité", desc: "Permettent la mesure et la personnalisation des campagnes. Ex : Google Ads (AW-10925515353)." },
  { key: "personalization", label: "Personnalisation", desc: "Adaptent les contenus à vos centres d'intérêt." },
];

function readConsent() {
  const m = document.cookie.split("; ").find((c) => c.startsWith(COOKIE_NAME + "="));
  if (!m) return null;
  try { return JSON.parse(decodeURIComponent(m.split("=")[1])); } catch { return null; }
}
function writeConsent(state) {
  const exp = new Date(Date.now() + COOKIE_DAYS * 86400_000).toUTCString();
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(state))}; Expires=${exp}; Path=/; SameSite=Lax`;
}

/** Défauts Google Consent Mode v2 poussés le plus tôt possible (avant chargement de gtag). */
function pushDefaultConsent() {
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;
  gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    personalization_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  gtag("set", "url_passthrough", true);
  gtag("set", "ads_data_redaction", true);
}

function applyConsent(state) {
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  gtag("consent", "update", {
    ad_storage:              state.advertising ? "granted" : "denied",
    ad_user_data:            state.advertising ? "granted" : "denied",
    ad_personalization:      state.personalization ? "granted" : "denied",
    analytics_storage:       state.analytics ? "granted" : "denied",
    personalization_storage: state.personalization ? "granted" : "denied",
  });
}

// Push defaults immédiatement au chargement du module (avant que gtag ne soit chargé)
// Note : index.html initialise déjà les defaults ; cette fonction est un fallback défensif
// pour les environnements où le script HTML ne s'est pas exécuté avant React.
if (typeof window !== "undefined" && !window.__dp_consent_default_pushed) {
  window.__dp_consent_default_pushed = true;
  pushDefaultConsent();
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [state, setState] = useState({ analytics: false, advertising: false, personalization: false });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setVisible(true);
    } else {
      applyConsent(existing);
    }
    // Exposer un moyen de rouvrir la bannière
    window.__openCookieBanner = () => { setVisible(true); setCustomize(false); };
  }, []);

  const acceptAll = useCallback(() => {
    const s = { analytics: true, advertising: true, personalization: true };
    writeConsent(s);
    applyConsent(s);
    setVisible(false);
  }, []);

  const rejectAll = useCallback(() => {
    const s = { analytics: false, advertising: false, personalization: false };
    writeConsent(s);
    applyConsent(s);
    setVisible(false);
  }, []);

  const saveCustom = useCallback(() => {
    writeConsent(state);
    applyConsent(state);
    setVisible(false);
    setCustomize(false);
  }, [state]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-0 bottom-0 z-[1000] p-4 md:p-6 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-white border-2 border-[#1C1C1C] rounded-2xl shadow-2xl p-5 md:p-6 pointer-events-auto">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 shrink-0 rounded-full bg-[#F0FBF6] flex items-center justify-center">
            <Cookie className="w-5 h-5 text-[#1AA66C]" />
          </div>
          <div className="flex-1">
            <h2 id="cookie-banner-title" className="font-heading font-bold text-lg md:text-xl text-[#2C3E50]">
              Vos préférences en matière de cookies
            </h2>
            <p className="text-sm text-[#5C6B7A] mt-1">
              Nous utilisons des cookies pour améliorer votre expérience et mesurer notre audience.
              Vous pouvez tout accepter, tout refuser ou personnaliser vos choix.
              Votre choix est conservé 6 mois et modifiable à tout moment via « Gérer mes cookies » en bas de page.
              Voir notre <a href="/cookies" className="text-[#1AA66C] font-semibold hover:underline">Politique cookies</a>.
            </p>
          </div>
        </div>

        {customize && (
          <div className="border-t border-[#E5E7EB] pt-4 mb-4 space-y-3" data-testid="cookie-customize">
            {CATEGORIES.map((cat) => (
              <label key={cat.key} className="flex items-start gap-3 p-3 rounded-xl border border-[#E5E7EB] hover:bg-[#F7F9FB] cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 w-4 h-4"
                  disabled={cat.locked}
                  checked={cat.locked ? true : Boolean(state[cat.key])}
                  onChange={(e) => setState((s) => ({ ...s, [cat.key]: e.target.checked }))}
                  data-testid={`cookie-cat-${cat.key}`}
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm text-[#2C3E50]">
                    {cat.label} {cat.locked && <span className="ml-2 text-[10px] uppercase text-[#5C6B7A]">toujours actif</span>}
                  </div>
                  <div className="text-xs text-[#5C6B7A]">{cat.desc}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={rejectAll}
            className="btn-outline !py-3 order-2 md:order-1"
            data-testid="cookie-reject-all"
          >
            Tout refuser
          </button>
          {!customize ? (
            <button
              type="button"
              onClick={() => setCustomize(true)}
              className="btn-outline !py-3 order-3 md:order-2 flex items-center justify-center gap-1"
              data-testid="cookie-customize-btn"
            >
              Personnaliser <ChevronDown className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={saveCustom}
              className="btn-outline !py-3 order-3 md:order-2"
              data-testid="cookie-save-custom"
            >
              Enregistrer mes choix
            </button>
          )}
          <button
            type="button"
            onClick={acceptAll}
            className="btn-primary !py-3 order-1 md:order-3"
            data-testid="cookie-accept-all"
          >
            Tout accepter
          </button>
        </div>
      </div>
    </div>
  );
}
